<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-shield-check-outline</v-icon>
      <span>Güvenli Operasyonlar</span>
      <v-spacer />
      <v-btn small color="primary" outlined class="mr-2" @click="loadList">
        <v-icon small left>mdi-refresh</v-icon>
        Yenile
      </v-btn>
      <v-btn
        small
        color="primary"
        outlined
        :to="{ name: 'admin.payment-ops.dashboard' }"
      >
        <v-icon small left>mdi-view-dashboard-outline</v-icon>
        Dashboard
      </v-btn>
    </v-card-title>

    <v-divider />

    <v-card-text>
      <v-alert dense outlined type="info" class="mb-4">
        Bu ekran yeni ödeme başlatmaz ve fallback denemesi oluşturmaz. Manuel
        status sorgusu sadece mevcut bir ödeme kaydı için provider durumunu
        kontrol eder; provider sonucu kesinleşmişse mevcut ödeme durumu
        güncellenebilir.
      </v-alert>

      <v-row dense class="mb-4">
        <v-col cols="12" md="4">
          <v-sheet outlined class="pa-3 payment-ops-safe-action-panel">
            <div class="subtitle-2 mb-3">Manuel Provider Status Sorgusu</div>
            <v-text-field
              v-model="statusQueryPaymentId"
              label="cc_payment_id"
              type="number"
              min="1"
              dense
              outlined
              hide-details="auto"
            />
            <v-btn
              block
              color="primary"
              class="mt-3"
              :loading="isStatusQueryLoading"
              :disabled="isStatusQueryLoading"
              @click="queryPaymentStatus"
            >
              <v-icon small left>mdi-cloud-search-outline</v-icon>
              Status Sorgula
            </v-btn>
          </v-sheet>
        </v-col>

        <v-col cols="12" md="8">
          <v-sheet outlined class="pa-3 payment-ops-safe-action-panel">
            <div class="subtitle-2 mb-3">Son Sorgu Sonucu</div>
            <div v-if="!statusQueryResult" class="text--secondary">
              Henüz manuel status sorgusu yapılmadı.
            </div>
            <v-simple-table v-else dense>
              <tbody>
                <tr>
                  <th>Payment ID</th>
                  <td>{{ statusQueryResult.payment.payment_id }}</td>
                  <th>Attempt</th>
                  <td>
                    {{ latestAttempt.id || "-" }}
                  </td>
                </tr>
                <tr>
                  <th>Payment Effects</th>
                  <td>
                    {{
                      paymentEffectsText(
                        statusQueryResult.payment.payment_effects_status
                      )
                    }}
                  </td>
                  <th>Attempt Status</th>
                  <td>
                    <v-chip
                      x-small
                      dark
                      :color="
                        providerAttemptStatusMeta(latestAttempt.status).color
                      "
                    >
                      {{ providerAttemptStatusMeta(latestAttempt.status).text }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <th>Success</th>
                  <td>{{ dateTime(statusQueryResult.payment.success_at) }}</td>
                  <th>Fail</th>
                  <td>{{ dateTime(statusQueryResult.payment.failed_at) }}</td>
                </tr>
                <tr>
                  <th>Provider Order</th>
                  <td>
                    {{ statusQueryResult.payment.provider_order_id || "-" }}
                  </td>
                  <th>Provider Transaction</th>
                  <td>
                    {{ latestAttempt.provider_transaction_id || "-" }}
                  </td>
                </tr>
                <tr>
                  <th>Açıklama</th>
                  <td colspan="3">
                    {{
                      latestAttempt.status_reason ||
                      latestActionLog.status_reason ||
                      "-"
                    }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-sheet>
        </v-col>
      </v-row>

      <v-row dense class="mb-4">
        <v-col cols="6" sm="4" md="3">
          <metric-card label="Toplam Aksiyon" :value="summary.item_count" />
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <metric-card label="Başladı" :value="summary.started_count" />
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <metric-card
            label="Başarılı"
            :value="summary.success_count"
            color="success"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <metric-card
            label="Hatalı"
            :value="summary.failed_count"
            color="error"
          />
        </v-col>
      </v-row>

      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        @update:items-per-page="handleItemsPerPageUpdated"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="Payment Ops Audit Log"
            icon="mdi-clipboard-text-clock-outline"
            :add-route="null"
            @clear-filters="handleClearFilters"
            @reload="loadList"
            hide-edit
            :search.sync="search.query"
            v-bind="titleBarAttrs"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.append="{ headers }">
          <rs-table-foot-totals
            :headers="headers"
            :totals="footTotals"
            v-if="list.length > 0"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.status="{ value }">
          <v-chip small dark :color="actionStatusMeta(value).color">
            {{ actionStatusMeta(value).text }}
          </v-chip>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.action="{ value }">
          {{ actionText(value) }}
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.cc_payment_id="{ value }">
          <router-link
            v-if="value"
            :to="{
              name: 'activities.cc-payments.list',
              query: { id: value },
            }"
          >
            {{ value }}
          </router-link>
          <span v-else>-</span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.cluster_name="{ item }">
          <router-link
            v-if="item.cluster_id"
            :to="{
              name: 'definitions.clusters.edit',
              params: { id: item.cluster_id },
              hash: '#cc',
            }"
          >
            {{ item.cluster_name }}
          </router-link>
          <span v-else>{{ item.cluster_name || "-" }}</span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.status_reason="{ value }">
          <span class="payment-ops-status-reason">{{ value || "-" }}</span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.created_at="{ value }">
          {{ dateTime(value) }}
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.updated_at="{ value }">
          {{ dateTime(value) }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasExport, hasPermissions, isPage } from "@/view/mixins";
import { mapGetters } from "vuex";

const emptySummary = () => ({
  item_count: 0,
  started_count: 0,
  success_count: 0,
  failed_count: 0,
});

export default {
  name: "PaymentOpsSafeActions",
  mixins: [filtersToURL, hasExport, hasPermissions, isPage],
  components: {
    MetricCard: {
      props: {
        label: {
          type: String,
          required: true,
        },
        value: {
          required: false,
          default: null,
        },
        color: {
          type: String,
          default: "primary",
        },
      },
      template: `
        <v-sheet outlined class="pa-3 payment-ops-safe-action-metric">
          <div class="caption text--secondary">{{ label }}</div>
          <div class="subtitle-1 font-weight-bold mt-2" :class="colorClass">
            {{ value !== null && value !== undefined ? value : 0 }}
          </div>
        </v-sheet>
      `,
      computed: {
        colorClass() {
          return `${this.color}--text`;
        },
      },
    },
  },
  computed: {
    ...mapGetters(["paymentProviderList"]),
    latestActionLog() {
      return this.statusQueryResult?.action_log || {};
    },
    latestAttempt() {
      return this.statusQueryResult?.latest_attempt || {};
    },
    summary() {
      return this.footTotals || emptySummary();
    },
  },
  data() {
    return {
      pageMeta: {
        title: "Güvenli Operasyonlar",
      },
      isStatusQueryLoading: false,
      statusQueryPaymentId: null,
      statusQueryResult: null,
      search: {
        query: null,
        cc_payment_id: null,
        payment_provider_ids: [],
        statuses: [],
        actions: [],
        created_at: {
          min: null,
          max: null,
        },
      },
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      dataTableAttrs: {
        footerProps: {
          disableItemsPerPage: false,
        },
      },
      actionOptions: [
        { id: "manual_status_query", text: "Status Sorgusu" },
        { id: "manual_commission_sync", text: "Komisyon Sync" },
        { id: "auto_charge_retry", text: "Otomatik Tahsilat Tekrarı" },
      ],
      statusOptions: [
        { id: "started", text: "Başladı" },
        { id: "success", text: "Başarılı" },
        { id: "failed", text: "Hatalı" },
      ],
      titleBarAttrs: {
        exportUrl: () => "api-super-admin/payment-ops/action-logs",
        exportParams: this.getParams,
      },
      headers: [
        {
          text: "Durum",
          value: "status",
          searchable: "multiselect",
          searchValue: "statuses",
          options: () => this.statusOptions,
          itemText: "text",
          width: "120px",
          notPrice: true,
        },
        {
          text: "Aksiyon",
          value: "action",
          searchable: "multiselect",
          searchValue: "actions",
          options: () => this.actionOptions,
          itemText: "text",
          width: "150px",
          notPrice: true,
        },
        {
          text: "Payment ID",
          value: "cc_payment_id",
          searchable: "text",
          width: "120px",
          notPrice: true,
        },
        {
          text: "Provider",
          value: "payment_provider_name",
          searchable: "multiselect",
          searchValue: "payment_provider_ids",
          options: () => this.paymentProviderList,
          width: "140px",
        },
        {
          text: "TYA",
          value: "cluster_name",
          searchable: "text",
          searchValue: "query",
          width: "220px",
        },
        {
          text: "Kullanıcı",
          value: "user_name",
          searchable: "text",
          width: "180px",
        },
        {
          text: "Attempt ID",
          value: "payment_provider_attempt_id",
          searchable: false,
          sortable: false,
          align: "end",
          width: "110px",
          notPrice: true,
        },
        {
          text: "Açıklama",
          value: "status_reason",
          searchable: "text",
          sortable: false,
          width: "260px",
          notPrice: true,
        },
        {
          text: "Oluşturma",
          value: "created_at",
          searchable: "date",
          searchValue: "created_at",
          width: "150px",
          notPrice: true,
        },
        {
          text: "Güncelleme",
          value: "updated_at",
          searchable: false,
          sortable: false,
          width: "150px",
          notPrice: true,
        },
      ],
    };
  },
  mounted() {
    this.generateExportColumns(this.headers);
    this.$nextTick(() => this.loadList());
  },
  methods: {
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      if (!this.search.created_at?.min && !this.search.created_at?.max) {
        delete params.created_at;
        delete params["created_at[min]"];
        delete params["created_at[max]"];
      }

      if (!this.search.cc_payment_id) {
        delete params.cc_payment_id;
      }

      return params;
    },
    loadList() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.list = [];
      this.footTotals = emptySummary();

      this.$api
        .query("api-super-admin/payment-ops/action-logs", {
          params: this.getParams(),
        })
        .then((response) => {
          this.loadListFromResponse(response);
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    queryPaymentStatus() {
      const paymentId = parseInt(this.statusQueryPaymentId, 10);

      if (!paymentId || paymentId < 1) {
        this.$toast.warning("Geçerli bir cc_payment_id girin.");
        return;
      }

      const confirmed = window.confirm(
        "Bu işlem yeni ödeme başlatmaz. Mevcut ödeme için provider status sorgusu yapılacak. Devam edilsin mi?"
      );

      if (!confirmed) {
        return;
      }

      this.isStatusQueryLoading = true;

      this.$api
        .post(`api-super-admin/payment-ops/payments/${paymentId}/status-query`)
        .then((response) => {
          this.statusQueryResult = response.data.data;
          this.$toast.success("Provider status sorgusu tamamlandı.");
          this.loadList();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isStatusQueryLoading = false));
    },
    handleClearFilters() {
      this.$refs.headSearch && this.$refs.headSearch.reset();
      this.$set(this.search, "cc_payment_id", null);
      this.$set(this.search, "payment_provider_ids", []);
      this.$set(this.search, "statuses", []);
      this.$set(this.search, "actions", []);
      this.$set(this.search, "created_at", {
        min: null,
        max: null,
      });
    },
    actionText(action) {
      const item = this.actionOptions.find((option) => option.id === action);

      return item ? item.text : action || "-";
    },
    actionStatusMeta(status) {
      const map = {
        started: { text: "Başladı", color: "info" },
        success: { text: "Başarılı", color: "success" },
        failed: { text: "Hatalı", color: "error" },
      };

      return map[status] || { text: status || "-", color: "grey" };
    },
    providerAttemptStatusMeta(status) {
      const map = {
        created: { text: "Created", color: "info" },
        pending: { text: "Pending", color: "warning" },
        success: { text: "Success", color: "success" },
        failed: { text: "Failed", color: "error" },
        unknown: { text: "Unknown", color: "warning" },
        cancelled: { text: "Cancelled", color: "grey" },
        manual_review: { text: "Manual Review", color: "deep-orange" },
      };

      return map[status] || { text: status || "-", color: "grey" };
    },
    paymentEffectsText(status) {
      const map = {
        pending: "Bekliyor",
        applying: "Uygulanıyor",
        applied: "Uygulandı",
        failed: "Hatalı",
        manual_review: "Manuel İnceleme",
      };

      return map[status] || status || "-";
    },
    dateTime(value) {
      return value ? new Date(value).toLocaleString("tr-TR") : "-";
    },
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.payment-ops-safe-action-panel,
.payment-ops-safe-action-metric {
  border-radius: 6px;
}

.payment-ops-safe-action-panel {
  min-height: 150px;
}

.payment-ops-safe-action-metric {
  min-height: 74px;
}

.payment-ops-status-reason {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
